// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import type { ColumnDef } from "@tanstack/react-table"
import { useAllEvolutionCycles } from "@/queries/queries.evolutionCycles"
import { useForges } from "@/queries/queries.forges"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"
import SmartTable from "@/components/ui/SmartTable"
import { CycleStatusBadge } from "@/views/evolutionCycles/cycleStatusBadge"
import { formatCurrency } from "@/utils"
import { formatDateTime } from "@/lib/formatDateTime"
import { CycleIdCell } from "@/views/evolutionCycles/cycleIdCell"
import { useTableUrlState } from "@/hooks/useTableUrlState"
import { useUrlParamWriter } from "@/hooks/useUrlParamWriter"
import {
  formatCycleDuration,
  durationSeconds,
} from "@/views/evolutionCycles/cycleDuration"

/**
 * A My-Cycles row. `total_cost` (cumulative cycle cost, ADR-0051) lives
 * on the ambient list-item type, pinned to the generated contract by
 * `src/test/wire-types-contract.guard`; it is nullable, so the Cost
 * column degrades gracefully ("—") when absent.
 */
type CycleRow = EvolutionCycleGlobalListItem

const selectClass =
  "h-9 rounded-[10px] border border-border-strong bg-surface px-3 text-[13px] text-text-secondary transition-colors hover:border-border-active focus:border-primary focus:outline-none"

// The table's sortable column ids, fed to the shared URL-state codec so a
// hostile `?sort=` decodes against the real column set (ADR-0006).
const CYCLE_COLUMN_IDS = [
  "cycle_id",
  "forge_name",
  "status",
  "started_at",
  "duration",
  "total_cost",
  "n_candidates",
] as const

/**
 * The global "My Cycles" page (`/evolution-cycles`).
 *
 * Lists every evolution cycle the signed-in user owns, across all their
 * forges, most recent first (server-guaranteed `started_at DESC`). Each
 * row links to that cycle's dashboard; the forge-name cell links to the
 * parent forge page. The page shows a visible count and degrades to an
 * empty table — never an error — when there are no cycles (PRD US §13).
 *
 * Reads the cross-forge list through `useAllEvolutionCycles` (the view
 * never touches axios directly — shared `apiClient` only, ADR-0011).
 *
 * Filtering (forge + status dropdowns) keeps the URL as the only source
 * of filter state (ADR-0006): each control reads from / writes to the
 * `forge_id` / `status` search params, so a filtered view is shareable,
 * survives reload, and round-trips through back/forward — no
 * component-local filter state. The table's sort (`?sort=`) and current
 * page (`?page=`) round-trip through the URL the same way, through the
 * shared `useTableUrlState` codec that every table binds — ADR-0006 rules
 * out state-only sort/pagination, and the codec makes the compliance
 * structural rather than per-view. Changing a filter or the sort resets
 * the page param. `started_after` has no UI control in v1 (PRD §Out of
 * Scope) but passes through from the URL to the request verbatim. The
 * forge dropdown is populated from the owner's forges (`useForges`); an
 * unknown `status` yields an empty list server-side, not a 400 (ADR-0022
 * status posture).
 */
const MyCyclesView = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const write = useUrlParamWriter()

  const forgeId = searchParams.get("forge_id") ?? undefined
  const status = searchParams.get("status") ?? undefined
  const startedAfter = searchParams.get("started_after") ?? undefined

  // Sort and current page live in the URL (ADR-0006) via the shared codec
  // binding — the same one My Forges and the Leaderboard use, so every
  // table's `?sort=` / `?page=` round-trips identically.
  const { sorting, onSortingChange, pageIndex, onPageIndexChange } =
    useTableUrlState({ columnIds: CYCLE_COLUMN_IDS })

  const params = useMemo(
    () => ({
      forge_id: forgeId,
      // The server treats an unknown status as an empty result (no 400),
      // so the route param is forwarded as-is (ADR-0022 status posture).
      status: status as CycleStatus | undefined,
      started_after: startedAfter,
    }),
    [forgeId, status, startedAfter],
  )

  const { data, isLoading, isError } = useAllEvolutionCycles(params)
  const { data: forgesData } = useForges()

  // Filter changes reset the page — page N of the previous result set is
  // meaningless against the new one, and the URL must stay coherent.
  const updateFilter = (key: string, value: string) =>
    write({ [key]: value, page: null })

  if (isLoading) {
    return <FullPageSpinnerWithText text="Loading your cycles" />
  }
  if (isError || !data) {
    return (
      <div
        role="alert"
        className="m-4 rounded-md border border-failed/40 bg-failed/10 p-4 text-failed-fg"
      >
        Could not load your cycles.
      </div>
    )
  }

  const items = (data.items ?? []) as CycleRow[]
  const forges = forgesData?.items ?? []

  const columns: ColumnDef<CycleRow>[] = [
    {
      id: "cycle_id",
      header: "Cycle ID",
      // Short, scannable form (PRD US §2); the full id stays recoverable via
      // hover title + click-to-copy (US §3). Sort keys off the full id so it
      // matches what the user copies, not the truncated display.
      accessorFn: (c) => c.cycle_id,
      cell: ({ row }) => <CycleIdCell id={row.original.cycle_id} />,
    },
    {
      id: "forge_name",
      header: "Forge",
      accessorFn: (c) => c.forge_name,
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left text-primary hover:underline"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/forges/${row.original.forge_id}`)
          }}
        >
          {row.original.forge_name}
        </button>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (c) => c.status,
      cell: ({ row }) => <CycleStatusBadge status={row.original.status} />,
    },
    {
      id: "started_at",
      header: "Started",
      accessorFn: (c) => c.started_at,
      cell: ({ row }) => formatDateTime(row.original.started_at),
    },
    {
      id: "duration",
      header: "Duration",
      // Replaces the old "Ended" column (PRD US §4): total elapsed for a
      // finished cycle, elapsed-so-far for a running one, a placeholder for a
      // not-yet-started cycle. Order by real elapsed seconds, not the string.
      accessorFn: (c) => durationSeconds(c.started_at, c.ended_at),
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {formatCycleDuration(row.original.started_at, row.original.ended_at)}
        </span>
      ),
    },
    {
      id: "total_cost",
      header: "Cost",
      // Additive backend field (US §1). Null/absent ⇒ placeholder, never a
      // misleading "$0.00"; the null sinks under the sort.
      accessorFn: (c) => c.total_cost ?? null,
      cell: ({ row }) =>
        row.original.total_cost == null ? (
          <span className="text-text-tertiary">—</span>
        ) : (
          <span className="font-mono text-xs">
            {formatCurrency(row.original.total_cost)}
          </span>
        ),
    },
    {
      id: "n_candidates",
      header: "Candidates",
      // Numeric ordering — the string fallback sorts "10" before "2".
      accessorFn: (c) => c.n_candidates,
      cell: ({ row }) => row.original.n_candidates,
    },
  ]

  return (
    <div className="flex flex-col gap-4 px-6 pb-16 pt-4">
      <div className="flex flex-row items-center gap-3">
        <h1 className="text-[25px] font-semibold tracking-[-0.015em] text-foreground">
          My Cycles
        </h1>
        <span className="font-mono text-xs text-text-tertiary">
          {items.length} cycle{items.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="flex flex-row flex-wrap items-center gap-3">
        <label
          htmlFor="forge-filter"
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-text-tertiary"
        >
          Forge
        </label>
        <select
          id="forge-filter"
          aria-label="Filter by forge"
          className={selectClass}
          value={forgeId ?? ""}
          onChange={(e) => updateFilter("forge_id", e.target.value)}
        >
          <option value="">All forges</option>
          {forges.map((forge) => (
            <option key={forge.forge_id} value={forge.forge_id ?? ""}>
              {forge.name}
            </option>
          ))}
        </select>
        <label
          htmlFor="status-filter"
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-text-tertiary"
        >
          Status
        </label>
        <select
          id="status-filter"
          aria-label="Filter by status"
          className={selectClass}
          value={status ?? ""}
          onChange={(e) => updateFilter("status", e.target.value)}
        >
          <option value="">All</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
        </select>
      </div>
      <SmartTable
        data={items}
        columns={columns}
        pageSize={20}
        sorting={sorting}
        onSortingChange={onSortingChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        onRowClick={(cycle) => navigate(`/evolution-cycles/${cycle.cycle_id}`)}
      />
    </div>
  )
}

export default MyCyclesView
